import React, { useState, useEffect, useCallback } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { showSuccess, showError } from "toast/toast";
import { useNavigate, useParams, Link } from "react-router-dom";
import Select, { SingleValue } from "react-select";
import { all } from "axios";
import { InstallationStatusBadge } from "components/Installationstatus";
import { StatusBadge } from "components/Productlocationstatus";
import { Addresses } from "types";

const mapContainerStyle = {
    width: "100%",
    height: "350px",
    borderRadius: "12px",
    overflow: "hidden",
    // boxShadow: "0 0 10px rgba(0,0,0,0.1)", // Soft shadow
    // border: "1px solid #dee2e6",          // Light border
};
import { getAuthCookie } from "../../utils/authCookies";
import {
    buildRemovalInstallerPayloadFields,
    resolveRemovalInstallerMembershipId,
} from "api/removalApi";


const center = {
    lat: 40.712776, // Default latitude (e.g., New York City)
    lng: -74.005974, // Default longitude
};

const AgentEditRemovalRequest = () => {
    const { id } = useParams(); // get ID from URL
    const token = getAuthCookie("sessionToken");
    const userId = getAuthCookie("userId");
    const userName = getAuthCookie("userName");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string>("");
    const [preview, setPreview] = useState<string | null>(null);
    const [formSubmitting, setFormSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number>(0);
    const [quantity, setQuantity] = useState(1);
    const [addressObjects, setAddressObjects] = useState<any[]>([]);
    const [removalOrder, setRemovalOrder] = useState<any>(null);
    const [updatedProducts, setUpdatedProducts] = useState<any[]>([]);
    const [removalQuantities, setRemovalQuantities] = useState<Record<string, number>>({});

    const [selectedLocation, setSelectedLocation] = useState<{
        lat: number;
        lng: number;
    } | null>(null);
    const { isLoaded } = useLoadScript({
        googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string, // Replace with your API key
    });

    const navigate = useNavigate();

    useEffect(() => {
        return () => {
            if (preview) URL.revokeObjectURL(preview);
        };
    }, [preview]);

    const addressOptions =
        addressObjects !== null
            ? addressObjects?.map((address) => ({
                ...address,
                value: address._id,
                label: `${address.label}: ${address.address_details.addressLine1}, ${address.address_details.city}, ${address.address_details.state}, ${address.address_details.zip ? address.address_details.zip : ''}`,
            }))
            : [];

    const priorityOptions = [
        { value: 1, label: "Critical" },
        { value: 2, label: "High Priority" },
        { value: 3, label: "Normal" },
    ];

    const [selectedBillingAddress, setSelectedBillingAddress] = useState<Addresses | null>(null);
    const [selectedRemovalAddress, setSelectedRemovalAddress] = useState<Addresses | null>(null);

    useEffect(() => {
        const fetchRemovalOrder = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "GET",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );

                const data = await response.json();

                if (!response.ok) {
                    showError(data.error || "Failed to fetch removal order");
                    return;
                }

                setRemovalOrder(data);
                setUpdatedProducts(data.line_items || []);
                console.log("Fetched removal order:", data);
                const initialQuantities: Record<string, number> = {};
                data.line_items?.forEach((item: any) => {
                    if (item.inventory_source !== "rotational") {
                        initialQuantities[item.product_id] = item.quantity_to_be_removed; // default quantity
                    }
                });
                setRemovalQuantities(initialQuantities);
            } catch (err) {
                // showError("Something went wrong while fetching removal order.");
                console.error(err);
            }
        };

        if (id) {
            fetchRemovalOrder();
        }
    }, [id]);

    useEffect(() => {
        if (removalOrder?.removal_details?.removal_address?.address_details?.latitude && removalOrder?.removal_details?.removal_address?.address_details?.longitude) {
            setSelectedLocation({
                lat: removalOrder.removal_details.removal_address.address_details.latitude,
                lng: removalOrder.removal_details.removal_address.address_details.longitude,
            });
        }
    }, [removalOrder]);

    useEffect(() => {
        const fetchAddresses = async () => {
            try {
                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/user-addresses/${userId}`,
                    {
                        method: "GET",
                        headers: {
                            Authorization: `Bearer ${token}`,
                            "Content-Type": "application/json",
                        },
                    }
                );
                const data = await response.json();
                console.log("Fetched Addresses:", data);
                setAddressObjects(data);
            } catch (err) {
                setError(
                    err instanceof Error ? err.message : "An unknown error occurred"
                );
            } finally {
                setLoading(false);
            }
        };

        fetchAddresses();
    }, [token]);

    // defult removal address set
    useEffect(() => {
        if (removalOrder) {
            const addr = removalOrder?.removal_details?.removal_address;
            setSelectedRemovalAddress(addr);
        }
        if (removalOrder?.billing_address) {
            setSelectedBillingAddress(removalOrder?.billing_address)
        }

    }, [removalOrder]);

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: {
            order_number: removalOrder?.order_number,
            priority: removalOrder?.priority || 0,
            scheduled_date: removalOrder?.removal_details?.scheduled_date.split("T")[0] || "",
            scheduled_jobs: 0,
            restock_warehouse: "",
            installeId: removalOrder?.removal_details?.installer_id,
            billing_address: "",
            officeId: removalOrder?.office?._id,
            removal_address: "",
            isSameAsBilling: false,
            billingAddressId: removalOrder?.billing_address?._id,
            removalAddressId: removalOrder?.removal_details?.removal_address?.label,
            location: "",
            products: "",
            instructions: "",
            payment_method: "",
            image: null,
            localLawsChecked: false,
            partial_amount: 0,
            transactionId: "",
            paymentMethod: "",
            installation_charge: removalOrder?.removal_cost,
            discount: removalOrder?.discount_amount || 0,
            subtotal: removalOrder?.subtotal_amount,
            total: removalOrder?.total_amount,
            tax: 0,
        },
        validationSchema: Yup.object({
            priority: Yup.string()
                .required("Removal priority is required"),

            // ✅ Removal Date must be entered
            scheduled_date: Yup.string()
                .required("Removal date is required"),

            // ✅ Billing Address must be selected
            billingAddressId: Yup.string()
                .required("Billing address is required"),
            // installer: Yup.string().required("Installer is required"),
            // installation_date: Yup.string().required("Installation date is required"),
            // billing_address: Yup.string().required("Billing address is required"),
            // installation_address: Yup.string().required(
            //     "Installation address is required"
            // ),
            // image: Yup.mixed().required("Image is required"),
        }),
        onSubmit: async (values) => {
            setFormSubmitting(true);
            try {
                // Example file upload handling
                const imageUrl = values.image ? await uploadFile(values.image) : null;

                const response = await fetch(
                    `${process.env.REACT_APP_API_URL}/removal_orders/${id}`,
                    {
                        method: "PUT",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify({
                            line_items: removalOrder?.line_items,
                            removal_details: {
                                scheduled_date: new Date(values.scheduled_date).toISOString(),
                                removal_address: removalOrder?.installation_details?.removal_address,
                                ...buildRemovalInstallerPayloadFields(removalOrder, null, {
                                    contactNumber: removalOrder?.removal_details?.contact_number,
                                }),
                                estimated_duration: null,
                                actual_removal_date: null,
                                removal_notes: [
                                    {
                                        note: values.instructions || "",
                                        added_by: {
                                            id: userId,
                                            name: userName,
                                        },
                                        added_at: new Date().toISOString(),
                                    },
                                ],
                                tax_details: null,
                            },
                            billing_address: selectedBillingAddress,
                            priority: Number(values.priority),
                            subtotal_amount: removalOrder?.subtotal_amount || 0,
                            tax_amount: 0,
                            total_amount: removalOrder?.total_amount || 0,
                            additional_instructions: [
                                {
                                    note: values.instructions || "",
                                    added_by: {
                                        id: userId,
                                        name: userName,
                                    },
                                    added_at: new Date().toISOString(),
                                },
                            ],
                        }),
                    }
                );

                if (!response.ok) {
                    const error = await response.json();
                    throw new Error(error.message || "Failed to create installation");
                }
                const data = await response.json();
                // navigate(`/agent/jobs/viewRemovalRequest/${data.order_id}`);
                showSuccess(data.message || "Removal request updated successfully!");
            } catch (error: any) {
                console.error(error);
                showError(`Error: ${error.message}`);
            } finally {
                setFormSubmitting(false);
                setUploadProgress(0);
            }
        },
    });

    const uploadFile = async (file: File): Promise<string> => {
        const formData = new FormData();
        formData.append("file", file);
        formData.append("uploadDir", "categories");

        return new Promise((resolve, reject) => {
            const xhr = new XMLHttpRequest();
            xhr.open("POST", `${process.env.REACT_APP_API_URL}/images/upload`, true);
            xhr.setRequestHeader("Authorization", `Bearer ${token}`);

            xhr.upload.onprogress = (event) => {
                if (event.lengthComputable) {
                    setUploadProgress((event.loaded / event.total) * 100);
                }
            };

            xhr.onload = () => {
                if (xhr.status === 200) {
                    const response = JSON.parse(xhr.responseText);
                    resolve(response.path);
                } else {
                    reject(new Error("Failed to upload file"));
                }
            };

            xhr.onerror = () => reject(new Error("Network error during file upload"));
            xhr.send(formData);
        });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.currentTarget.files?.[0];
        if (file) {
            formik.setFieldValue("image", file);
            setPreview(URL.createObjectURL(file));
        }
    };

    const handleLocationChange = (
        selectedOption: SingleValue<{ value: string; label: string }>
    ) => {
        formik.setFieldValue("location", selectedOption?.value || "");
    };

    const handleMapClick = useCallback((event: google.maps.MapMouseEvent) => {
        if (event.latLng) {
            const lat = event.latLng.lat();
            const lng = event.latLng.lng();
            setSelectedLocation({ lat, lng });
            formik.setFieldValue("location", { lat, lng });
        }
    }, []);

    if (!isLoaded) return <div>Loading...</div>;


    return (
        <div className="content">
            <div className="page-container">
                <h3 className="m-0 text-center fw-bold">Edit Removal Request </h3>
            </div>
            <div className="card">
                <div className="card-body">
                    <div className="row">
                        <div className="col-md-12">
                            <h4 className="table-headers">Removal Request Status : <InstallationStatusBadge value={removalOrder?.status ?? ""} type="order" /></h4>
                            <hr />
                            {/* <div>
                                        <h4>Removal Request Status : {
                                            removalOrder?.status === 100
                                                ? "Draft"
                                                : removalOrder?.status === 200
                                                    ? "Finalized"
                                                    : removalOrder?.status === 300
                                                        ? "Scheduled"
                                                        : removalOrder?.status === 400
                                                            ? "In Progress"
                                                            : removalOrder?.status === 500
                                                                ? "Completed"
                                                                : removalOrder?.status === 600
                                                                    ? "Canceled"
                                                                    : removalOrder?.status === 700
                                                                        ? "Losted"
                                                                        : "Unknown"
                                        }</h4>
                                    </div> */}
                        </div>
                    </div>
                    <form onSubmit={formik.handleSubmit}>
                        <div className="row d-flex justify-content-center">
                            <div className="col-md-2 mb-3">
                                <label className="form-label table-cells">
                                    Removal Job Order
                                </label>
                                <input type="text" name="order_number" id="order_number" className="form-control" placeholder="Enter Installation Job Order Number" value={`${removalOrder?.order_number || "—"}`} disabled />
                                {/* {formik.touched.order_number && formik.errors.order_number && (
                                            <div className="text-danger">{formik.errors.order_number}</div>
                                        )} */}
                            </div>

                            <div className="col-md-2 mb-3">
                                <label className="form-label table-cells">
                                    Removal Priority <span className="text-danger">*</span>
                                </label>
                                <Select
                                    id="priority"
                                    name="priority"
                                    options={priorityOptions}
                                    value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
                                    onChange={(option) =>
                                        formik.setFieldValue("priority", option?.value || "")
                                    }
                                    onBlur={formik.handleBlur}
                                />
                                {formik.touched.priority && typeof formik.errors.priority === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.priority}</div>
                                )}
                            </div>

                            <div className="col-md-2 mb-3">
                                <label className="form-label table-cells">
                                    Removal Date <span className="text-danger">*</span>
                                </label>
                                <input
                                    type="date"
                                    className="form-control"
                                    id="scheduled_date"
                                    name="scheduled_date"
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    value={formik.values.scheduled_date}
                                />
                                {formik.touched.scheduled_date && typeof formik.errors.scheduled_date === "string" && (
                                    <div className="invalid-feedback">{formik.errors.scheduled_date}</div>
                                )}
                            </div>
                            {/* <div className="col-md-6 mb-3">
                                    <label className="form-label">
                                        Assigned To
                                    </label>
                                    <input type="text" className="form-control" value="John" disabled />
                                </div> */}
                        </div>
                        <div className="dashed-line my-2"></div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h3 className="table-headers">Address details</h3>
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="billingAddressId"
                                    className="form-label table-cells"
                                >
                                    Billing Address <span className="text-danger">*</span>
                                </label>
                                <Select
                                    // className="form-control"
                                    id="billingAddress"
                                    name="billingAddress"
                                    options={addressOptions}
                                    value={
                                        addressOptions?.find(
                                            (option) =>
                                                option.value === formik.values.billingAddressId
                                        ) || null
                                    }
                                    onChange={(selected) => {
                                        console.log("Selected Billing Address:", selected); // Debugging log
                                        formik.setFieldValue("billingAddressId", selected?.value);
                                        const selectedAddress = addressObjects?.find((addr) => addr._id === selected?.value);
                                        setSelectedBillingAddress(selectedAddress);
                                    }}
                                />
                                {formik.touched.billingAddressId && typeof formik.errors.billingAddressId === "string" && (
                                    <div className="text-danger mt-1">{formik.errors.billingAddressId}</div>
                                )}
                            </div>
                            <div className="col-sm-4">
                                <label
                                    htmlFor="removalAddressId"
                                    className="form-label table-cells"
                                >
                                    Removal Address
                                </label>
                                <input
                                    type="text"
                                    className="form-control"
                                    id="removalAddressId"
                                    name="removalAddressId"
                                    value={`${removalOrder?.removal_details?.removal_address?.addressLine1}, ${removalOrder?.removal_details?.removal_address?.city}, ${removalOrder?.removal_details?.removal_address?.state}`}
                                    disabled
                                />
                            </div>
                        </div>
                        <hr />
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h3 className="text-center mb-4">Map View</h3>
                                <div style={mapContainerStyle}>
                                    <GoogleMap
                                        mapContainerStyle={mapContainerStyle}
                                        zoom={10}
                                        center={selectedLocation || center}
                                    // onClick={handleMapClick}
                                    >
                                        {selectedLocation && <Marker position={selectedLocation} />}
                                    </GoogleMap>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <h4 className="invoice-subheaders text-center">Product details </h4>

                                <div className="row mb-5">
                                    <div className="col-md-12">
                                        <table className="table table-striped ">
                                            <thead className="table-light">
                                                <tr>
                                                    <th className="text-center table-headers" style={{ width: "20%" }}>Product Name</th>
                                                    <th className="text-center table-headers" style={{ width: "8%" }}>SKU</th>
                                                    <th className="text-center table-headers" style={{ width: "8%" }}> Qty </th>
                                                    <th className="text-center table-headers" style={{ width: "8%" }}>Qty to remove </th>
                                                    <th className="text-center table-headers" style={{ width: "15%" }}>Product Location Status</th>
                                                    {/* <th className="text-center table-headers" style={{ width: "9%" }}>Status</th> */}
                                                    {/* <th className="text-center">Product Name</th>
                                                            <th className="text-center">SKU</th>
                                                            <th className="text-center">Available Quantity </th>
                                                            <th className="text-center">Quantity to remove </th>
                                                            <th className="text-center">Product Location Status</th> */}
                                                    {/* <th className="text-center">Action</th> */}
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational")?.map((item: any, index: number) => (
                                                    <tr key={index}>
                                                        <td className="text-center table-cells text-ellipsis">{item.product_name}</td>
                                                        <td className="text-center table-cells">{item.sku}</td>
                                                        <td className="text-center table-cells">{item.quantity_to_be_removed}</td>
                                                        <td className="text-center table-cells">
                                                            <input
                                                                type="number"
                                                                className="form-control text-center table-cells"
                                                                value={removalQuantities[item.product_id] || 0}
                                                                disabled
                                                                // min={1}
                                                                onChange={(e) => {
                                                                    const inputVal = parseInt(e.target.value) || 1;
                                                                    const clampedVal = Math.min(inputVal, item.quantity_to_be_removed); // ✅ cap at max available

                                                                    setRemovalQuantities((prev) => ({
                                                                        ...prev,
                                                                        [item.product_id]: clampedVal,
                                                                    }));
                                                                }}
                                                            />
                                                        </td>
                                                        <td className="text-center table-cells">
                                                            <span className="">
                                                                <StatusBadge status={item.current_status?.replace(/_/g, " ") || ""} />

                                                                {/* {item.current_status} */}
                                                            </span>
                                                        </td>
                                                        {/* <td className="text-center">
                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-success"
                                                                    >
                                                                        Remove
                                                                    </button>
                                                                </td> */}
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        </div>
                        <div className="row mb-4 align-items-start">
                            {/* Instructions */}
                            <div className="col-md-6">
                                <label className="table-headers mb-2">Additional Instructions</label>
                                <textarea
                                    name="instructions"
                                    id="instructions"
                                    className="form-control"
                                    rows={4}
                                    placeholder="Enter additional instructions"
                                    value={formik.values.instructions}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                ></textarea>
                            </div>

                            {/* Gate Code */}
                            <div className="col-md-2">
                            </div>
                            <div className="col-md-1"></div>
                            {/* Summary Table */}
                            <div className="col-md-3 mt-4 mt-md-0 text-end">
                                <table className="table table-sm text-center align-middle mb-0 text-end">
                                    <tbody>
                                        <tr>
                                            <td className="table-headers"><strong>Removal Cost:</strong></td>
                                            <td className="table-cells">${Number(formik.values.installation_charge).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="table-headers"><strong>Subtotal:</strong></td>
                                            <td className="table-cells">${Number(formik.values.subtotal).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="table-headers"><strong>Tax:</strong></td>
                                            <td className="table-cells">${Number(formik.values.tax).toFixed(2)}</td>
                                        </tr>
                                        <tr>
                                            <td className="table-headers"><strong>Total:</strong></td>
                                            <td className="table-cells fw-bold">${Number(formik.values.total).toFixed(2)}</td>
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                        <div className="dashed-line my-2"></div>
                        <div className="row mb-3 rcust-margin-4">
                            <div className="col-md-12 d-flex justify-content-between align-items-start">
                                <button
                                    className="btn btn-outline-secondary br-8 backbutton"
                                    type="button"
                                    onClick={() => navigate(-1)}
                                >
                                    Back
                                </button>

                                <div className="text-end">
                                    <div className="form-check ms-2 mt-3">
                                        <input
                                            type="checkbox"
                                            id="localLaws"
                                            name="localLawsChecked"
                                            className="form-check-input me-2"
                                            checked={formik.values.localLawsChecked}
                                            onChange={formik.handleChange}
                                            onBlur={formik.handleBlur}
                                        />
                                        <label className="form-check-label table-cells" htmlFor="localLaws">
                                            I acknowledge adherence to local laws
                                        </label>
                                    </div>
                                    <button
                                        type="submit"
                                        className="btn btn-info"
                                        disabled={formSubmitting || !formik.values.localLawsChecked}
                                    >
                                        Submit Request
                                    </button>
                                </div>
                            </div>
                        </div>

                    </form>
                </div>
            </div >
        </div >
    );
};

export default AgentEditRemovalRequest;





//  <div className="content">
//         <div className="card">
//             <div className="card-body">
//                 <h2>Edit Removal Request </h2>
//                 <div className="row">
//                     <div className="col-md-6">
//                         <div>
//                             <h4>Removal Request Status : {
//                                 removalOrder?.status === 100
//                                     ? "Draft"
//                                     : removalOrder?.status === 200
//                                         ? "Finalized"
//                                         : removalOrder?.status === 300
//                                             ? "Scheduled"
//                                             : removalOrder?.status === 400
//                                                 ? "In Progress"
//                                                 : removalOrder?.status === 500
//                                                     ? "Completed"
//                                                     : removalOrder?.status === 600
//                                                         ? "Canceled"
//                                                         : removalOrder?.status === 700
//                                                             ? "Losted"
//                                                             : "Unknown"
//                             }</h4>
//                         </div>
//                     </div>
//                 </div>
//                 <form onSubmit={formik.handleSubmit}>
//                     <div className="row mt-5">
//                         <div className="col-md-6 mb-3">
//                             <label className="form-label">
//                                 Removal Job Order
//                             </label>
//                             <input type="text" name="order_number" id="order_number" className="form-control" placeholder="Enter Installation Job Order Number" value={`${removalOrder?.order_number || "—"}`} disabled />
//                             {/* {formik.touched.order_number && formik.errors.order_number && (
//                                 <div className="text-danger">{formik.errors.order_number}</div>
//                             )} */}
//                         </div>

//                         <div className="col-md-3 mb-3">
//                             <label className="form-label">
//                                 Removal Priority
//                             </label>
//                             <Select
//                                 id="priority"
//                                 name="priority"
//                                 options={priorityOptions}
//                                 value={priorityOptions.find((option) => option.value === formik.values.priority) || null}
//                                 onChange={(option) =>
//                                     formik.setFieldValue("priority", option?.value || "")
//                                 }
//                                 onBlur={formik.handleBlur}
//                             />
//                             {/* {formik.touched.priority && formik.errors.priority && (
//                                 <div className="text-danger">{formik.errors.priority}</div>
//                             )} */}
//                         </div>

//                         <div className="col-md-3 mb-3">
//                             <label className="form-label">
//                                 Removal Date
//                             </label>
//                             <input
//                                 type="date"
//                                 className="form-control"
//                                 id="scheduled_date"
//                                 name="scheduled_date"
//                                 onChange={formik.handleChange}
//                                 onBlur={formik.handleBlur}
//                                 value={formik.values.scheduled_date}
//                             />
//                             {/* {formik.touched.removal_date &&
//                                 formik.errors.removal_date && (
//                                     <div className="invalid-feedback">
//                                         {formik.errors.removal_date}
//                                     </div>
//                                 )} */}
//                         </div>
//                         {/* <div className="col-md-6 mb-3">
//                             <label className="form-label">
//                                 Assigned To
//                             </label>
//                             <select name="assigned_to" id="assigned_to" className="form-control">
//                                 <option value="">Select </option>
//                                 <option value="John" selected>John</option>
//                                 <option value="David">David</option>
//                             </select>
//                         </div> */}
//                     </div>

//                     <div className="row mb-3">
//                         <div className="col-md-12">
//                             <h3>Address details</h3>
//                         </div>
//                         <div className="col-sm-6">
//                             <label
//                                 htmlFor="billingAddressId"
//                                 className="form-label"
//                             >
//                                 Billing Address
//                             </label>
//                             <Select
//                                 className="form-control"
//                                 id="billingAddress"
//                                 name="billingAddress"
//                                 options={addressOptions}
//                                 value={
//                                     addressOptions?.find(
//                                         (option) =>
//                                             option.value === formik.values.billingAddressId
//                                     ) || null
//                                 }
//                                 onChange={(selected) => {
//                                     console.log("Selected Billing Address:", selected); // Debugging log
//                                     formik.setFieldValue("billingAddressId", selected?.value);
//                                     const selectedAddress = addressObjects?.find(
//                                         (addr) => (addr.address_details ? addr.address_details._id : addr._id) === selected?.value
//                                     );
//                                     setSelectedBillingAddress(selectedAddress?.address_detailsected);
//                                 }}
//                             />
//                         </div>
//                         <div className="col-sm-6">
//                             <label
//                                 htmlFor="removalAddressId"
//                                 className="form-label"
//                             >
//                                 Removal Address
//                             </label>
//                             <input
//                                 type="text"
//                                 className="form-control"
//                                 id="removalAddressId"
//                                 name="removalAddressId"
//                                 value={`${removalOrder?.removal_details?.removal_address?.addressLine1}, ${removalOrder?.removal_details?.removal_address?.city}, ${removalOrder?.removal_details?.removal_address?.state}`}
//                                 disabled
//                             />
//                         </div>
//                     </div>
//                     <div className="row mb-3">
//                         <div className="col-md-12">
//                             <h3 className="text-center mt-">Map View</h3>
//                             <div style={mapContainerStyle}>
//                                 <GoogleMap
//                                     mapContainerStyle={mapContainerStyle}
//                                     zoom={10}
//                                     center={selectedLocation || center}
//                                 // onClick={handleMapClick}
//                                 >
//                                     {selectedLocation && <Marker position={selectedLocation} />}
//                                 </GoogleMap>
//                             </div>
//                         </div>
//                     </div>
//                     <div className="row mb-3">
//                         <div className="col-md-12">
//                             <h4>Product details </h4>

//                             <div className="row mb-5">
//                                 <div className="col-md-12">
//                                     <table>
//                                         <thead>
//                                             <tr>
//                                                 <th className="text-center">Product Name</th>
//                                                 <th className="text-center">SKU</th>
//                                                 <th className="text-center">Available Quantity </th>
//                                                 <th className="text-center">Quantity to remove </th>
//                                                 <th className="text-center">Product Location Status</th>
//                                                 <th className="text-center">Action</th>
//                                             </tr>
//                                         </thead>
//                                         <tbody>
//                                             {updatedProducts?.filter((item: any) => item.inventory_source !== "rotational").map((item: any, index: number) => (
//                                                 <tr key={index}>
//                                                     <td className="text-center">{item.product_name}</td>
//                                                     <td className="text-center">{item.sku}</td>
//                                                     <td className="text-center">{item.available_quantity}</td>
//                                                     <td className="text-center">
//                                                         <input
//                                                             type="number"
//                                                             className="form-control text-center"
//                                                             value={removalQuantities[item.product_id] || 0}
//                                                             disabled
//                                                             // min={1}
//                                                             onChange={(e) => {
//                                                                 const inputVal = parseInt(e.target.value) || 1;
//                                                                 const clampedVal = Math.min(inputVal, item.quantity_to_be_removed); // ✅ cap at max available

//                                                                 setRemovalQuantities((prev) => ({
//                                                                     ...prev,
//                                                                     [item.product_id]: clampedVal,
//                                                                 }));
//                                                             }}
//                                                         />
//                                                     </td>
//                                                     <td className="text-center">
//                                                         <span className="bg-warning p-2 rounded-4">{item.current_status}</span>
//                                                     </td>
//                                                     {/* <td className="text-center">
//                                                         <button
//                                                             type="button"
//                                                             className="btn btn-success"
//                                                         >
//                                                             Remove
//                                                         </button>
//                                                     </td> */}
//                                                 </tr>
//                                             ))}
//                                         </tbody>
//                                     </table>
//                                 </div>

//                             </div>

//                         </div>
//                     </div>

//                     <div className="row mb-3">
//                         <div className="col-md-6">
//                             <div className="mb-3">
//                                 <label htmlFor="">Additional Instructions </label>
//                                 <textarea
//                                     name="instructions"
//                                     id="instructions"
//                                     className="form-control"
//                                     rows={4}
//                                     placeholder="Enter additional instructions"
//                                     value={formik.values.instructions}
//                                     onChange={formik.handleChange}
//                                     onBlur={formik.handleBlur}
//                                 ></textarea>
//                             </div>
//                         </div>
//                         <div className="col-md-6">
//                             <table className="calculation-table mb-3">
//                                 <tr>
//                                     <th>Removal Fee</th>
//                                     <th>${Number(formik.values.installation_charge).toFixed(2)}</th>
//                                 </tr>
//                                 <tr>
//                                     <th>Subtotal</th>
//                                     <th>${Number(formik.values.subtotal).toFixed(2)}</th>
//                                 </tr>
//                                 <tr>
//                                     <th>Tax</th>
//                                     <td>${Number(formik.values.tax).toFixed(2)}</td>
//                                 </tr>
//                                 <tr>
//                                     <th>Total</th>
//                                     <td>${Number(formik.values.total).toFixed(2)}</td>
//                                 </tr>
//                             </table>
//                         </div>
//                     </div>

//                     <div className="row justify-content-end">
//                         <div className="col-md-3 mt-3">
//                             <label className="me-1 mb-1">Local Laws Compliance :</label><br />
//                             <input
//                                 type="checkbox"
//                                 id="localLaws"
//                                 name="localLawsChecked"
//                                 checked={formik.values.localLawsChecked}
//                                 onChange={formik.handleChange}
//                                 onBlur={formik.handleBlur}
//                             />
//                             <span>{" "}I acknowledge adherence to local laws</span>

//                         </div>
//                     </div>

//                     <button
//                         type="submit"
//                         className="btn btn-info mt-4 float-end"
//                         disabled={formSubmitting || !formik.values.localLawsChecked}
//                     >
//                         Submit Request
//                     </button>
//                 </form>
//             </div>
//         </div >
//     </div >